import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { INSIGHTS } from "@/data/content";
import { ARTICLES } from "@/data/articles";
import { readTime } from "@/lib/articleText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const toSlug = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const cardCls =
  "group relative flex h-full flex-col bg-[#0D1220] p-7 text-left transition-[background-color,transform,box-shadow] duration-500 hover:-translate-y-1 hover:bg-[#111827] hover:shadow-[0_24px_48px_-24px_rgba(0,0,0,0.6)]";

const CardInner = ({ article }) => (
  <>
    <span
      className="absolute right-4 top-4 h-3 w-3 border-r border-t border-[#F26122]/0 transition-colors duration-500 group-hover:border-[#F26122]/70"
      aria-hidden="true"
    />
    <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#F2824F]">
      {article.slug ? article.readTime : "Coming soon"}
    </span>
    <h3 className="mt-5 font-display text-xl font-normal leading-snug text-slate-100 transition-colors duration-300 group-hover:text-[#F26122]">
      {article.title}
    </h3>
    <p className="mt-4 flex-1 text-sm font-light leading-relaxed text-slate-500">
      {article.excerpt}
    </p>
    {article.slug ? (
      <span className="mt-6 flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500 transition-colors duration-300 group-hover:text-[#F26122]">
        Read the perspective
        <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </span>
    ) : (
      <span className="mt-6 font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-600">
        Perspective · Jeet Advisory
      </span>
    )}
  </>
);

const Insights = () => {
  const [live, setLive] = useState(ARTICLES);

  useEffect(() => {
    axios
      .get(`${API}/articles`)
      .then(({ data }) => {
        if (data.articles?.length) {
          setLive(
            data.articles.map((a) => ({
              ...a,
              readTime: readTime(a.body || ""),
            }))
          );
        }
      })
      .catch(() => {});
  }, []);

  const liveTitles = new Set(live.map((a) => a.title));
  const cards = [
    ...live.map((a) => ({
      title: a.title,
      slug: a.slug,
      excerpt: a.excerpt,
      readTime: a.readTime,
    })),
    ...INSIGHTS.filter((i) => !liveTitles.has(i.title)),
  ];

  return (
    <section
      id="insights"
      data-testid="insights-section"
      className="border-t border-white/5 bg-[#0D1220]"
    >
      <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
        <SectionHeading number="08" label="Insights" title="Perspectives on capital" tone="dark" />

        <div className="mt-16 grid grid-cols-1 gap-px border border-white/10 bg-[#0D1220]/10 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((article, i) => (
            <Reveal key={article.title} delay={i * 0.06} className="h-full">
              {article.slug ? (
                <Link
                  to={`/insights/${article.slug}`}
                  data-testid={`insight-card-${toSlug(article.title)}`}
                  className={cardCls}
                >
                  <CardInner article={article} />
                </Link>
              ) : (
                <article
                  data-testid={`insight-card-${toSlug(article.title)}`}
                  className={`${cardCls} cursor-default`}
                >
                  <CardInner article={article} />
                </article>
              )}
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Insights;
